import type { DeltaPolarity, StatusRole } from '@lighthouse/design'
import type { HealthCheckResponse } from '../../types/api'
import type { CombSummary } from '../../types/dataComb'
import type { EtlHalt } from '../../types/etlHalts'
import type { EtlRunListItem } from '../../types/etlRuns'
import type { LiveOperation, LiveOperations, LiveRun } from '../../types/liveOperations'
import type { SourceHealthOverview, WorkerFleet } from '../../types/observatory'
import type { EtlJobCatalog } from '../../types/etlJobs'
import type { Throughput, ThroughputTotals } from '../../types/throughput'
import { haltConsequenceText, haltReasonText } from '../jobs/requestFormat'
import { healthRole } from '../health/healthBoardFormat'
import { outcomeDisplay, percentOfWindow } from '../etl-explorer/runFormat'
import {
  NOT_MEASURED, agoLabel, compactNumber, duration, errorKind, errorSummary, exactNumber,
} from '../../utils/format'

/**
 * The Overview's pure helpers: every rule a panel applies to a served payload before drawing it.
 *
 * Pure and vitest-safe on purpose — types, utils/format and two sibling format modules that are
 * themselves pure. Nothing here imports services/api.ts (MSAL reads `window` at load) or a hook.
 *
 * THE RULE every function here keeps: a value the server did not measure renders NOT_MEASURED,
 * never 0 and never green. A measured zero is a real reading and is printed as one.
 */

// ---------------------------------------------------------------------------
// Query state
// ---------------------------------------------------------------------------

/** The slice of a React Query result these helpers read. */
export interface QueryLike<T> {
  data: T | undefined
  isLoading: boolean
  isError: boolean
  error: unknown
}

/**
 * Loading, error or ready — error FIRST.
 *
 * A background refetch that fails keeps the previous payload in `data`, and that payload is no
 * longer what the server says; drawing it as current is how a panel shows yesterday's all-clear
 * over a request that did not come back. Then `isLoading || !data`, never `isLoading` alone.
 */
export function queryView<T>(q: QueryLike<T>): 'error' | 'loading' | 'ready' {
  if (q.isError) return 'error'
  if (q.isLoading || q.data === undefined) return 'loading'
  return 'ready'
}

/** The one-line text of a failed request, by kind. Never the raw body. */
export function requestErrorText(what: string, error: unknown): string {
  const kind = errorKind(error)
  if (kind === 'forbidden') return `The server refused to serve ${what} to this caller (403).`
  if (kind === 'unauthorized') return `Not signed in, so ${what} could not be read (401).`
  if (kind === 'network') return `Could not reach the API to read ${what}.`
  const said = errorSummary(error)
  return said ? `Could not read ${what}: ${said}` : `Could not read ${what}.`
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${exactNumber(n)} ${n === 1 ? one : many}`
}

// ---------------------------------------------------------------------------
// Signals
// ---------------------------------------------------------------------------

/*
 * The signal store is built (Automaton's detector writes it; `api/observatory/signals` serves it), and every rule
 * the Overview applies to it — the Issues and Critical tiles, the Signal Inbox preview, the rail badge — lives in
 * components/signals/signalFormat.ts beside the Inbox that owns it. The two "not built" constants that stood here
 * are gone: an Overview still saying "not built" over a live store would be the stale all-clear in reverse.
 */

// ---------------------------------------------------------------------------
// /api/health
// ---------------------------------------------------------------------------

/*
 * healthRole, healthGroups, HEALTH_GROUPS and HealthGroup live in components/health/healthBoardFormat.ts
 * since the Health Monitoring page became their second reader. Re-exported here so every Overview
 * importer keeps its import; the cycle this makes is safe only while neither module calls the other at
 * load (see that module's header).
 */
export { HEALTH_GROUPS, healthGroups, healthRole } from '../health/healthBoardFormat'
export type { HealthGroup } from '../health/healthBoardFormat'

/** The KPI band's System Health tile. */
export interface HealthKpi {
  value: string
  /** Absent whenever nothing was measured — no colour for a non-answer. */
  status: StatusRole | undefined
  sublabel: string
}

/**
 * The served aggregate status, and how many checks stood behind it.
 *
 * No percentage: there is no formula that turns five named checks into "97% healthy", and the old
 * tile's figure came from a fixture. The status is the API's own aggregate — the worst entry — and
 * the sublabel says whose view it is, because a check the API runs is the API's reachability, not
 * the worker's.
 */
export function healthKpi(q: QueryLike<HealthCheckResponse>): HealthKpi {
  const view = queryView(q)
  if (view === 'error') {
    return { value: NOT_MEASURED, status: undefined, sublabel: requestErrorText('the API health report', q.error) }
  }
  if (view === 'loading') return { value: NOT_MEASURED, status: undefined, sublabel: 'asking the API…' }
  const report = q.data!
  const entries = Object.values(report.entries ?? {})
  const healthy = entries.filter((e) => e.status === 'Healthy').length
  const role = healthRole(report.status)
  return {
    value: role ? report.status : report.status || NOT_MEASURED,
    status: role ?? undefined,
    sublabel: entries.length === 0
      ? 'the API reported no checks'
      : `${healthy} of ${entries.length} checks healthy, as seen by the API`,
  }
}

// ---------------------------------------------------------------------------
// Workers and sources
// ---------------------------------------------------------------------------

/** A System Health row that is not a /api/health entry. A null status is a dash with its caption. */
export interface HealthLine {
  status: StatusRole | null
  label: string
  detail: string | null
}

/**
 * The Workers row, from the worker projection.
 *
 * Order matters and follows the Workers page: a fleet that has never reported, and a projection
 * nobody is refreshing, are NOT answers about the workers — they are dashes, never green and never
 * red. Only then does the fleet itself speak.
 */
export function workersLine(fleet: WorkerFleet): HealthLine {
  if (fleet.hasNeverReported) {
    return { status: null, label: 'never reported', detail: 'No worker has published a heartbeat to this database.' }
  }
  if (fleet.isProjectionStale) {
    return {
      status: null,
      label: 'stale',
      detail: `Automaton last published ${agoLabel(fleet.observedAt)}; these figures are history.`,
    }
  }
  if (fleet.liveWorkerCount === 0) {
    return { status: 'critical', label: 'no live worker', detail: 'Every worker has stopped beating.' }
  }
  if (fleet.unresponsiveWorkerCount > 0) {
    return {
      status: 'warning',
      label: `${exactNumber(fleet.unresponsiveWorkerCount)} unresponsive`,
      detail: `${plural(fleet.liveWorkerCount, 'live worker')}; ${plural(fleet.unresponsiveWorkerCount, 'worker')} stopped beating.`,
    }
  }
  return { status: 'good', label: `${exactNumber(fleet.liveWorkerCount)} live`, detail: null }
}

/**
 * The Sources row, from the source-health projection.
 *
 * `hasNeverReported` is checked FIRST, as the type's own comment demands: the never-reported
 * envelope carries `isProjectionStale: false` and all three counts at zero, so asking about
 * staleness first renders an empty projection as healthy and fresh.
 */
export function sourcesLine(overview: SourceHealthOverview): HealthLine {
  if (overview.hasNeverReported) {
    return { status: null, label: 'never reported', detail: 'No host has published a source reading.' }
  }
  if (overview.isProjectionStale) {
    return {
      status: null,
      label: 'stale',
      detail: `Last published ${agoLabel(overview.observedAt)}; nothing is refreshing these readings.`,
    }
  }
  if (overview.faultedCount > 0) {
    return { status: 'critical', label: `${exactNumber(overview.faultedCount)} faulted`, detail: `${exactNumber(overview.usableCount)} usable.` }
  }
  if (overview.unobservedCount > 0) {
    return {
      status: null,
      label: `${exactNumber(overview.unobservedCount)} not observed`,
      detail: `${exactNumber(overview.usableCount)} usable; nothing has asked the rest anything recently.`,
    }
  }
  if (overview.sources.length === 0) {
    return { status: null, label: 'no sources', detail: 'The projection lists no source connections.' }
  }
  return { status: 'good', label: `${exactNumber(overview.usableCount)} usable`, detail: null }
}

/** The KPI band's Active Jobs tile. */
export interface ActiveJobsKpi {
  value: string
  sublabel: string
}

/**
 * Busy worker slots — what Hangfire's servers report executing — NOT a count of ETL runs.
 *
 * A never-reported fleet or a stale projection is a dash: the number would be history. There is
 * never a delta: no earlier sample is kept to compare against.
 */
export function activeJobsKpi(q: QueryLike<WorkerFleet>): ActiveJobsKpi {
  const view = queryView(q)
  if (view === 'error') return { value: NOT_MEASURED, sublabel: requestErrorText('the worker fleet', q.error) }
  if (view === 'loading') return { value: NOT_MEASURED, sublabel: 'asking the worker projection…' }
  const fleet = q.data!
  if (fleet.hasNeverReported) return { value: NOT_MEASURED, sublabel: 'no worker has reported' }
  if (fleet.isProjectionStale) return { value: NOT_MEASURED, sublabel: 'worker projection is stale' }
  return { value: exactNumber(fleet.busyWorkerSlots), sublabel: 'busy worker slots · no prior window' }
}

/** Worker Utilization's headline tile. */
export interface FleetUtilizationTile {
  value: string
  sublabel: string
}

/**
 * The fleet's utilization headline — a rate, so only printed where there is capacity to measure.
 *
 * - A stale projection is a dash, as the Active Jobs tile dashes the same reading: every figure
 *   in it is history. The panel's warning line below still says so.
 * - No live worker, or no slots, is a dash too. The server returns 0 for a rate over zero slots
 *   (ObservatoryWorkersController.Percent), and "0%" reads as "idle and available" — the opposite
 *   of a fleet that has stopped beating, and the reading an operator would act on wrongly.
 */
export function fleetUtilizationTile(fleet: WorkerFleet): FleetUtilizationTile {
  if (fleet.hasNeverReported) return { value: NOT_MEASURED, sublabel: 'no worker has reported' }
  if (fleet.isProjectionStale) {
    const when = fleet.observedAt ? ` — last published ${agoLabel(fleet.observedAt)}` : ''
    return { value: NOT_MEASURED, sublabel: `worker projection is stale${when}` }
  }
  if (fleet.liveWorkerCount === 0 || fleet.totalWorkerSlots === 0) {
    return { value: NOT_MEASURED, sublabel: 'no live worker — no capacity to measure' }
  }
  return {
    value: `${fleet.utilizationPercent}%`,
    sublabel: `${exactNumber(fleet.busyWorkerSlots)} of ${exactNumber(fleet.totalWorkerSlots)} slots busy across `
      + `${plural(fleet.liveWorkerCount, 'live worker')}`,
  }
}

// ---------------------------------------------------------------------------
// Live operations
// ---------------------------------------------------------------------------

/**
 * What a live entity run's newest chunk is doing — never "Queued".
 *
 * `EtlRunStep` reads Queued for the whole of a live run, so the old
 * `humanizePhase(phase ?? 'Queued')` rendered every extracting entity as queued. The phase comes
 * from the newest chunk; null (no chunk landing or transforming, or a stale row) is a dash.
 */
export function livePhaseText(op: Pick<LiveOperation, 'livePhase' | 'liveChunkIndex'>): string {
  const verb = op.livePhase === 'landing' ? 'Landing' : op.livePhase === 'transforming' ? 'Transforming' : null
  if (!verb) return NOT_MEASURED
  return op.liveChunkIndex == null ? verb : `${verb} chunk ${exactNumber(op.liveChunkIndex + 1)}`
}

/**
 * "resumed (attempt 2)" for a row a resume reopened, else null. Its `startedAt` is the FIRST
 * attempt's, so its timer counts the idle gap between attempts; this says why it reads long.
 */
export function resumedText(op: Pick<LiveOperation, 'attemptCount'>): string | null {
  return op.attemptCount > 1 ? `resumed (attempt ${exactNumber(op.attemptCount)})` : null
}

/**
 * Seconds from `startedAt` to now, measured on the SERVER's clock.
 *
 * `observedAt` is the server instant the payload was computed at and `fetchedAtMs` the browser
 * instant it arrived (React Query's `dataUpdatedAt`); the browser clock only measures the time
 * since. A skewed browser clock therefore moves nothing. `nowMs` is null before the first tick,
 * which reads as "at the answer". Null when the row never recorded a start.
 *
 * The same rule as hooks/useEtlActivity.ts `elapsedSince` (the Live Activity banner). It is not
 * imported from there because that module pulls in services/api.ts and so MSAL, which this pure
 * module must not.
 */
export function elapsedSeconds(
  startedAt: string | null,
  observedAt: string,
  fetchedAtMs: number,
  nowMs: number | null,
): number | null {
  if (startedAt == null) return null
  const start = Date.parse(startedAt)
  const observed = Date.parse(observedAt)
  if (Number.isNaN(start) || Number.isNaN(observed)) return null
  const sinceAnswer = nowMs == null || fetchedAtMs <= 0 ? 0 : Math.max(0, nowMs - fetchedAtMs)
  return Math.max(0, Math.floor((observed - start + sinceAnswer) / 1000))
}

/** The elapsed readout, or a dash for a row that never recorded a start. */
export function elapsedText(seconds: number | null): string {
  return seconds == null ? NOT_MEASURED : duration(seconds)
}

/**
 * A stale row's one readout: when it was last heard from. No ticking timer — nothing is running.
 *
 * Phrased around agoLabel's "12m ago" (it already carries "ago", see utils/format.ts).
 */
export function staleSincePhrase(lastHeartbeatAt: string | null, startedAt: string | null): string {
  if (lastHeartbeatAt != null) return `last heard from ${agoLabel(lastHeartbeatAt)}`
  if (startedAt != null) return `never beat — started ${agoLabel(startedAt)}`
  return 'no heartbeat or start recorded'
}

/** {@link staleSincePhrase} as a sentence of its own. */
export function staleSinceText(lastHeartbeatAt: string | null, startedAt: string | null): string {
  const phrase = staleSincePhrase(lastHeartbeatAt, startedAt)
  return phrase.charAt(0).toUpperCase() + phrase.slice(1)
}

/** The progress readout under a live entity run. */
export type CoverageBar =
  | { kind: 'determinate'; percent: number; label: string }
  | { kind: 'indeterminate'; reason: string | null }
  | { kind: 'none'; reason: string | null }

/**
 * A determinate bar ONLY from a served window share, labelled as a share of the date WINDOW —
 * never of rows, which nothing measures before a chunk lands. Without a share the bar is
 * indeterminate while the row is live and absent when it is stale: an animated bar on a dead
 * worker's row would claim the activity it lacks.
 *
 * FLOORED, never rounded, and worded by the Explorer's own runFormat.percentOfWindow, because the
 * row links there and the two must read the same: a live row at 99.6% of its window is not "100%"
 * (finished), and one at 0.4% is not "0%" (nothing done) — it reads "<0.1%" only below a tenth.
 * The bar's width is floored to the same tenth, so it never draws past what the label says.
 */
export function coverageBar(op: Pick<LiveOperation, 'windowCoverage' | 'isHeartbeatStale'>): CoverageBar {
  const share = op.windowCoverage.share
  if (share != null && Number.isFinite(share)) {
    const clamped = Math.min(1, Math.max(0, share))
    const percent = Math.floor(clamped * 1000) / 10
    return { kind: 'determinate', percent, label: `${percentOfWindow(clamped)}% of the date window` }
  }
  const reason = op.windowCoverage.unavailableReason
  return op.isHeartbeatStale ? { kind: 'none', reason } : { kind: 'indeterminate', reason }
}

/**
 * One run's entity counts, as row counts — never a percentage. `TotalEntities` is written once at
 * creation and a resume by the other job kind can push rows past it, so finished-over-total can
 * pass 100%; `entitiesNotStarted` is the server's honest substitute and a dash when it withheld it.
 */
export function runCountsLine(run: LiveRun): string {
  const parts = [
    `${exactNumber(run.entitiesFinished)} finished`,
    `${exactNumber(run.entitiesRunning)} running`,
  ]
  if (run.entitiesStale > 0) parts.push(`${exactNumber(run.entitiesStale)} stale`)
  parts.push(`${run.entitiesNotStarted == null ? NOT_MEASURED : exactNumber(run.entitiesNotStarted)} not started`)
  if (run.countsMixResumedJobs) parts.push('counts mix resumed jobs')
  return parts.join(' · ')
}

/** A stale run's warning line; null for a live one. */
export function runStaleNote(run: LiveRun): string | null {
  if (!run.isStale) return null
  return `No heartbeat — ${staleSincePhrase(run.lastHeartbeatAt, run.startedAt)}`
}

/**
 * How many stale RUNS the server holds: its uncapped `staleRunCount`. An API that predates the
 * field sends none, so then it is the listed ones plus any it says it left out.
 */
export function staleRunTotal(data: LiveOperations): number {
  if (typeof data.staleRunCount === 'number') return data.staleRunCount
  return data.runs.filter((r) => r.isStale).length + (data.staleRunsNotListed ?? 0)
}

/** How many stale rows the Live Operations panel lists before it summarises the rest. */
export const STALE_ROWS_SHOWN = 3

/** The Live Operations panel's partition of the payload. */
export interface LiveOperationsView {
  liveRuns: LiveRun[]
  staleRuns: LiveRun[]
  /** Stale runs held by the server but not drawn here — the panel's cap, then the server's. */
  staleRunsHidden: number
  liveOps: LiveOperation[]
  staleOps: LiveOperation[]
  staleOpsHidden: number
  /** Live entity runs the server's 50-row cap left out. */
  liveOpsNotListed: number
  /** Nothing at all is recorded as running, stale or not. */
  isEmpty: boolean
  /** A non-stale entity run exists, so the elapsed timers advance. */
  ticking: boolean
}

/**
 * Live work first and in full; stale work capped at `STALE_ROWS_SHOWN` with the remainder counted.
 * Stale rows accumulate for all time — nothing closes a dead worker's rows — so listing every one
 * would bury the work actually in flight. The hidden counts come from the server's uncapped totals,
 * so nothing vanishes without being counted.
 */
export function liveOperationsView(
  data: LiveOperations,
  shown = STALE_ROWS_SHOWN,
): LiveOperationsView {
  const liveRuns = data.runs.filter((r) => !r.isStale)
  const staleRunsAll = data.runs.filter((r) => r.isStale)
  const staleRuns = staleRunsAll.slice(0, shown)
  const liveOps = data.operations.filter((o) => !o.isHeartbeatStale)
  const staleOpsAll = data.operations.filter((o) => o.isHeartbeatStale)
  const staleOps = staleOpsAll.slice(0, shown)
  const liveOpTotal = Math.max(0, data.operationCount - data.staleOperationCount)
  const runTotal = typeof data.runCount === 'number' ? data.runCount : data.runs.length
  return {
    liveRuns,
    staleRuns,
    staleRunsHidden: Math.max(0, staleRunTotal(data) - staleRuns.length),
    liveOps,
    staleOps,
    staleOpsHidden: Math.max(0, data.staleOperationCount - staleOps.length),
    liveOpsNotListed: Math.max(0, liveOpTotal - liveOps.length),
    isEmpty: data.operationCount === 0 && runTotal === 0 && data.runs.length === 0,
    ticking: liveOps.length > 0,
  }
}

// ---------------------------------------------------------------------------
// Critical Attention: three measured conditions
// ---------------------------------------------------------------------------

export type ConditionKey = 'halts' | 'stale' | 'comb'

/**
 * - `attention`: measured, and something needs a human.
 * - `clear`: measured, and nothing does.
 * - `unmeasured`: the source answered, but not with a measurement (the comb never ran, or its
 *   latest pass did not complete) — never counted as clear.
 */
export type ConditionState = 'loading' | 'error' | 'unmeasured' | 'clear' | 'attention'

export interface AttentionCondition {
  key: ConditionKey
  state: ConditionState
  /** The condition's name. */
  label: string
  /** Colour for an `attention` row; null otherwise. */
  tone: 'critical' | 'warning' | null
  /** What the row says. */
  title: string
  detail: string | null
  consequence: string | null
  /** Where the row's action goes — an in-app route, navigated with useNavigate. */
  target: string
  actionLabel: string
  /** ISO instant the row's age is measured from; null when there is none. */
  occurredAt: string | null
}

const HALT_LABEL = 'Active ETL halts'
const STALE_LABEL = 'Stale ETL runs'
const COMB_LABEL = 'Critical Data Comb findings'

function pending(key: ConditionKey, label: string, target: string, actionLabel: string): AttentionCondition {
  return {
    key, state: 'loading', label, tone: null, title: label, detail: null, consequence: null,
    target, actionLabel, occurredAt: null,
  }
}

function failed(
  key: ConditionKey, label: string, what: string, error: unknown, target: string, actionLabel: string,
): AttentionCondition {
  return {
    key, state: 'error', label, tone: null, title: requestErrorText(what, error), detail: null,
    consequence: null, target, actionLabel, occurredAt: null,
  }
}

/**
 * Active halts, from `GET api/etl/halts` (blocking only, newest first).
 *
 * What the halt stops is the server's answer, not a sentence kept here: `catalog` is the job
 * catalogue (the query the rail's admin gate already holds), whose active-halt summary carries the
 * categories a halt lets through — requestFormat.haltConsequenceText, the Scheduler's own wording.
 * Until the catalogue answers, it says only that the halt blocks runs.
 */
export function haltCondition(q: QueryLike<EtlHalt[]>, catalog?: EtlJobCatalog): AttentionCondition {
  const target = '/jobs'
  const view = queryView(q)
  if (view === 'error') return failed('halts', HALT_LABEL, 'ETL halts', q.error, target, 'Open the Scheduler')
  if (view === 'loading') return pending('halts', HALT_LABEL, target, 'Open the Scheduler')
  const blocking = q.data!.filter((h) => h.isBlocking !== false && h.acknowledgedAt == null)
  if (blocking.length === 0) {
    return {
      key: 'halts', state: 'clear', label: HALT_LABEL, tone: null, title: 'No active ETL halt',
      detail: null, consequence: null, target, actionLabel: 'Open the Scheduler', occurredAt: null,
    }
  }
  const newest = blocking[0]
  return {
    key: 'halts',
    state: 'attention',
    label: HALT_LABEL,
    tone: 'critical',
    title: blocking.length === 1 ? 'ETL is halted' : `ETL is halted — ${exactNumber(blocking.length)} active halts`,
    detail: `${blocking.length === 1 ? '' : 'Newest: '}${haltReasonText(newest.reason)} on ${newest.runKey}.`,
    consequence: haltConsequenceText(catalog),
    target,
    actionLabel: 'Review in the Scheduler',
    occurredAt: newest.haltedAt,
  }
}

/** The stale sentence, with the oldest age the server measured. */
export function staleDetail(data: LiveOperations): string {
  const rule = `${exactNumber(data.staleRule.minutes)} min${data.staleRule.published ? '' : ' (code default)'}`
  return `Recorded as running with no heartbeat for more than ${rule} `
    + `(oldest ${agoLabel(data.oldestStaleSince)} — nothing closes or trims a run that never finished).`
}

/**
 * Stale runs and stale entity runs, from Live Operations — the server's uncapped counts, judged by
 * the worker's own published rule.
 *
 * The link goes where the stale thing is listed: a stale RUN is an Execution History outcome; an
 * entity run can be stale under a run that is not (another entity in it still beats), and only the
 * Explorer lists entity runs.
 */
export function staleCondition(q: QueryLike<LiveOperations>): AttentionCondition {
  const view = queryView(q)
  const runsTarget = '/execution-history?status=stale'
  if (view === 'error') return failed('stale', STALE_LABEL, 'live operations', q.error, runsTarget, 'View stale runs')
  if (view === 'loading') return pending('stale', STALE_LABEL, runsTarget, 'View stale runs')
  const data = q.data!
  const runs = staleRunTotal(data)
  const ops = data.staleOperationCount
  if (runs === 0 && ops === 0) {
    return {
      key: 'stale', state: 'clear', label: STALE_LABEL, tone: null, title: 'No stale run',
      detail: null, consequence: null, target: runsTarget, actionLabel: 'View stale runs', occurredAt: null,
    }
  }
  const parts: string[] = []
  if (runs > 0) parts.push(plural(runs, 'stale run'))
  if (ops > 0) parts.push(plural(ops, 'stale entity run'))
  return {
    key: 'stale',
    state: 'attention',
    label: STALE_LABEL,
    tone: 'warning',
    title: parts.join(' · '),
    detail: staleDetail(data),
    consequence: null,
    target: runs > 0 ? runsTarget : '/etl-explorer',
    actionLabel: runs > 0 ? 'View stale runs' : 'View entity runs',
    occurredAt: data.oldestStaleSince,
  }
}

/** Pass statuses whose counts cover the whole sweep. Anything else ended early. */
const COMPLETE_PASS = new Set(['Succeeded', 'SucceededWithWarnings'])

/**
 * Outstanding critical findings on the latest completed Data Comb pass.
 *
 * VERIFIED (DataCombController.GetSummary): `severities` counts the latest completed pass's
 * findings with `AcknowledgedAt == null` — outstanding ones — grouped by severity, and omits a
 * severity with none. So a missing `critical` entry is a measured zero on that pass.
 *
 * Two answers are not zero: the comb never having run (`latestRun` null), and a latest pass that
 * did not complete — a failed sweep's zero covers only the tables it reached.
 */
export function combCondition(q: QueryLike<CombSummary>): AttentionCondition {
  const target = '/data-comb'
  const view = queryView(q)
  if (view === 'error') return failed('comb', COMB_LABEL, 'the Data Comb summary', q.error, target, 'Open the Data Comb')
  if (view === 'loading') return pending('comb', COMB_LABEL, target, 'Open the Data Comb')
  const summary = q.data!
  const run = summary.latestRun
  if (!run) {
    return {
      key: 'comb', state: 'unmeasured', label: COMB_LABEL, tone: null,
      title: 'The Data Comb has never run',
      detail: 'Findings are not measured until its first pass completes.',
      consequence: null, target, actionLabel: 'Open the Data Comb', occurredAt: null,
    }
  }
  const critical = summary.severities.find((s) => s.severity === 'critical')?.count ?? 0
  const complete = COMPLETE_PASS.has(run.status)
  const when = run.completedAt ?? run.startedAt
  if (critical === 0) {
    if (!complete) {
      return {
        key: 'comb', state: 'unmeasured', label: COMB_LABEL, tone: null,
        title: `No critical finding on a pass that ended ${run.status}`,
        detail: `Pass ${run.runKey} did not complete, so its zero covers only the tables it reached.`,
        consequence: null, target, actionLabel: 'Open the Data Comb', occurredAt: when,
      }
    }
    return {
      key: 'comb', state: 'clear', label: COMB_LABEL, tone: null,
      title: 'No outstanding critical finding',
      detail: `On the latest Data Comb pass, ${run.runKey}.`,
      consequence: null, target, actionLabel: 'Open the Data Comb', occurredAt: when,
    }
  }
  return {
    key: 'comb',
    state: 'attention',
    label: COMB_LABEL,
    tone: 'critical',
    title: `${plural(critical, 'outstanding critical finding')}`,
    detail: `On the latest Data Comb pass, ${run.runKey}${complete ? '' : ` (ended ${run.status}, so it may not have covered every table)`}.`,
    consequence: null,
    target,
    actionLabel: 'Review findings',
    occurredAt: when,
  }
}

/** The ONLY all-clear the Overview says — qualified, because only three things are measured. */
export const ALL_CLEAR_TEXT =
  'Nothing measured needs attention: no active halt, no stale run, no outstanding critical finding.'

/** All-clear only when every condition is MEASURED clear; a loading, failed or unmeasured one is not. */
export function isAllClear(conditions: readonly AttentionCondition[]): boolean {
  return conditions.length > 0 && conditions.every((c) => c.state === 'clear')
}

// ---------------------------------------------------------------------------
// Recent executions
// ---------------------------------------------------------------------------

export type OutcomeIcon = 'done' | 'warning' | 'failed' | 'running' | 'neutral'

export interface RecentOutcome {
  label: string
  icon: OutcomeIcon
}

/**
 * An entity run's outcome as the Explorer labels it (runFormat.outcomeDisplay — the row's link
 * goes there, so the two must agree), with an icon. `stale` is never the running icon: it is a
 * worker that died holding the row. An unknown outcome is itself on a neutral icon.
 */
export function recentOutcome(outcome: string): RecentOutcome {
  const display = outcomeDisplay(outcome)
  if (outcome === 'running') return { label: display.label, icon: 'running' }
  if (display.tone.kind === 'neutral') return { label: display.label, icon: 'neutral' }
  switch (display.tone.role) {
    case 'good': return { label: display.label, icon: 'done' }
    case 'critical': return { label: display.label, icon: 'failed' }
    default: return { label: display.label, icon: 'warning' }
  }
}

/** When a recent run happened: its completion, else its start, else a dash. */
export function recentWhen(run: Pick<EtlRunListItem, 'outcome' | 'startedAt' | 'completedAt'>): string {
  if (run.completedAt) return agoLabel(run.completedAt)
  if (run.startedAt) return `started ${agoLabel(run.startedAt)}`
  return NOT_MEASURED
}

// ---------------------------------------------------------------------------
// Throughput
// ---------------------------------------------------------------------------

/** The Throughput panel's window, in hours. The subtitle and the request both read it. */
export const THROUGHPUT_HOURS = 24

/** Said instead of a flat zero line: no run finished, so there is no series to draw. */
export const NO_RUNS_TEXT = `No ETL runs finished in the last ${THROUGHPUT_HOURS} hours — every ETL job is manual-trigger.`

/** Percentage change, or null when either side is unmeasured or the base is zero. */
export function percentChange(current: number | null, previous: number | null): number | null {
  if (current == null || previous == null || previous === 0) return null
  return ((current - previous) / previous) * 100
}

/** A served percentage: whole numbers as they are, anything else to one decimal. */
export function percentText(value: number | null): string {
  if (value == null) return NOT_MEASURED
  return Number.isInteger(value) ? `${value}%` : `${value.toFixed(1)}%`
}

export interface ThroughputTile {
  key: 'records' | 'rejected' | 'success' | 'duration'
  label: string
  value: string
  /** Null: draw no delta. Present only when a prior window exists and the change is computable. */
  delta: { change: number; polarity: DeltaPolarity; label?: string } | null
  sublabel: string | null
}

function tileDelta(
  current: number | null,
  previous: number | null,
  prior: ThroughputTotals | null,
  polarity: DeltaPolarity,
  priorText: string,
): Pick<ThroughputTile, 'delta' | 'sublabel'> {
  if (prior == null) return { delta: null, sublabel: 'no prior window' }
  const change = percentChange(current, previous)
  if (change == null) return { delta: null, sublabel: `prior window: ${priorText}` }
  return { delta: { change, polarity }, sublabel: 'vs the prior window' }
}

/**
 * The four tiles under the throughput series. A delta ONLY when the server served a prior window
 * (`previous` non-null) — otherwise "no prior window", never a 0% change. Success rate is run
 * grain and moves in percentage POINTS, so its delta is a difference, labelled as points.
 */
export function throughputTiles(t: Throughput): ThroughputTile[] {
  const now = t.totals
  const prior = t.previous
  const records = tileDelta(now.recordsProcessed, prior?.recordsProcessed ?? null, prior, 'higher-is-better',
    prior ? compactNumber(prior.recordsProcessed) : NOT_MEASURED)
  const rejected = tileDelta(now.recordsRejected, prior?.recordsRejected ?? null, prior, 'lower-is-better',
    prior ? exactNumber(prior.recordsRejected) : NOT_MEASURED)
  const avg = tileDelta(now.avgDurationSeconds, prior?.avgDurationSeconds ?? null, prior, 'lower-is-better',
    prior?.avgDurationSeconds != null ? duration(prior.avgDurationSeconds) : NOT_MEASURED)

  let success: Pick<ThroughputTile, 'delta' | 'sublabel'>
  if (prior == null) success = { delta: null, sublabel: 'no prior window' }
  else if (now.successRatePercent == null || prior.successRatePercent == null) {
    success = { delta: null, sublabel: `prior window: ${percentText(prior.successRatePercent)}` }
  } else {
    const change = now.successRatePercent - prior.successRatePercent
    success = {
      delta: { change, polarity: 'higher-is-better', label: `${Math.abs(change).toFixed(1)} pts` },
      sublabel: 'vs the prior window',
    }
  }

  return [
    { key: 'records', label: 'Records processed', value: compactNumber(now.recordsProcessed), ...records },
    { key: 'rejected', label: 'Rejected records', value: exactNumber(now.recordsRejected), ...rejected },
    { key: 'success', label: 'Success rate', value: percentText(now.successRatePercent), ...success },
    {
      key: 'duration', label: 'Avg duration',
      value: now.avgDurationSeconds == null ? NOT_MEASURED : duration(now.avgDurationSeconds), ...avg,
    },
  ]
}

/** A mini panel's latest-hour readout; the last bucket is the hour in progress, so it says so. */
export function latestHourText(value: number | null | undefined, format: (v: number) => string): string {
  return `${value == null ? NOT_MEASURED : format(value)} this hour so far`
}

// ---------------------------------------------------------------------------
// Top entities
// ---------------------------------------------------------------------------

export const TOP_ENTITY_COUNT = 5

/** The window, in UTC calendar days including today — MetricsController.WindowStart. */
export const TOP_ENTITY_DAYS = 7

/**
 * The subtitle, driven by the same constant the request sends. The volumes are the activity log's
 * RecordsProcessed — the final attempt's counters — so a resumed load under-counts, and it says so.
 */
export const TOP_ENTITIES_SUBTITLE =
  `Last ${TOP_ENTITY_DAYS} UTC days, today included · records from successful loads · `
  + 'a resumed load counts its final attempt only'
